import { createContext, useContext, useMemo, useState, type ReactNode } from "react";
import type { Id } from "@convex/_generated/dataModel";

type ImpersonationContextValue = {
  impersonatedPersonId: Id<"persons"> | null;
  setImpersonatedPersonId: (personId: Id<"persons"> | null) => void;
  clearImpersonation: () => void;
};

// Per tab, not per browser: impersonating different people in different tabs
// is how roles get tested side by side, and with localStorage every reload
// (or hot reload) picked up whichever tab had chosen last. sessionStorage
// survives a reload and is copied into a duplicated tab.
const STORAGE_KEY = "cemmbase.impersonatedPersonId";

const ImpersonationContext = createContext<ImpersonationContextValue | null>(null);

export function ImpersonationProvider({ children }: { children: ReactNode }) {
  // Read on the first render rather than in an effect, so a reload does not
  // first render the whole page as the real viewer and then switch.
  const [impersonatedPersonId, setImpersonatedPersonIdState] = useState<Id<"persons"> | null>(
    () => window.sessionStorage.getItem(STORAGE_KEY) as Id<"persons"> | null,
  );

  const value = useMemo<ImpersonationContextValue>(
    () => ({
      impersonatedPersonId,
      setImpersonatedPersonId(personId) {
        setImpersonatedPersonIdState(personId);
        if (personId) {
          window.sessionStorage.setItem(STORAGE_KEY, personId);
        } else {
          window.sessionStorage.removeItem(STORAGE_KEY);
        }
      },
      clearImpersonation() {
        setImpersonatedPersonIdState(null);
        window.sessionStorage.removeItem(STORAGE_KEY);
      },
    }),
    [impersonatedPersonId],
  );

  return <ImpersonationContext.Provider value={value}>{children}</ImpersonationContext.Provider>;
}

export function useImpersonation() {
  const context = useContext(ImpersonationContext);
  if (!context) {
    throw new Error("useImpersonation must be used within an ImpersonationProvider");
  }

  return context;
}

export function useEffectivePersonId() {
  const { impersonatedPersonId } = useImpersonation();
  return impersonatedPersonId;
}
